import { Alert, Button } from '@economic/taco';
import { Trans } from '@lingui/macro';
import useSwr from 'swr';
import { Form } from '~/app/shared/components/Form';
import payrollApiClient from '~/app/shared/util/api/payrollApiClient';
import { useEffect, useState } from 'react';
import type { CompanyResponse } from '~/payment-cards-feature';
import { getCompanyInfoStepValidationSchema, treatPhoneNumber } from '../../utils/utils';
import { CannotValidateCompanyDialog } from './CannotValidateCompanyDialog';
import { CheckCVRSeNumberResponse, CompanyInfoStepState } from '../../types';
import { CompanyInfoFormFields } from './CompanyInfoFormFields';
import { usePayrollOnboardingContext } from '../../context/OnboardingContext';

const checkCvrOrSeNumberFetcher = (url: string) => payrollApiClient.get(url).then(response => response.data);

export const DialogContent = ({
    currentCompanyData,
    onSettingsSaved,
}: {
    currentCompanyData: CompanyResponse;
    onSettingsSaved: () => void;
}) => {
    const { setCompanyInfoStepState, onNext, setIsDialogLoading } = usePayrollOnboardingContext();
    const { model: currentCompanyDataModel } = currentCompanyData;
    const { callingCode: companyCallingCode, number: companyPhoneNumber } = treatPhoneNumber(currentCompanyDataModel.phone);
    const [showCompanyFailedToValidateDialog, setShowCompanyFailedToValidateDialog] = useState(false);
    const [readyToSubmit, setReadyToSubmit] = useState(false);

    const { data: checkCvrOrSeNumberData, isLoading: isCVRCheckLoading } = useSwr<CheckCVRSeNumberResponse>(
        `/onboarding/check-cvr-se-number?cvrSeNumber=${currentCompanyDataModel.cvr}`,
        checkCvrOrSeNumberFetcher
    );

    // The "check CVR/SE number" API call might take some time, so to improve user experience, we do it
    // right after this component is mounted and when the user clicks "Next" we only submit the form
    // if the API call has finished.
    // This way, the user doesn't have to wait for the API call to finish before they can fill out the form.
    //
    // This useEffect will do exactly that
    useEffect(() => {
        if (!readyToSubmit) {
            return;
        }

        if (isCVRCheckLoading) {
            setIsDialogLoading(true);
            return;
        }

        setIsDialogLoading(false);

        if (checkCvrOrSeNumberData?.isValidCVR || checkCvrOrSeNumberData?.isValidSE) {
            setCompanyInfoStepState(currentState => ({
                ...(currentState || ({} as CompanyInfoStepState)),
                isCvr: checkCvrOrSeNumberData.isValidCVR,
                isSe: checkCvrOrSeNumberData.isValidSE,
            }));
            onNext();
        } else {
            setReadyToSubmit(false);
            setShowCompanyFailedToValidateDialog(true);
        }
    }, [checkCvrOrSeNumberData, isCVRCheckLoading, onNext, readyToSubmit, setCompanyInfoStepState, setIsDialogLoading]);

    const formInitialData: CompanyInfoStepState = {
        cvrOrSENumber: currentCompanyDataModel.cvr,
        companyName: currentCompanyDataModel.name,
        emailForInvoicing: currentCompanyDataModel.invoiceToEmail,
        companyCallingCode,
        companyPhoneNumber,
        postCode: currentCompanyDataModel.zipCode,
        city: currentCompanyDataModel.city,
        address: currentCompanyDataModel.address1,
        contactPersonName: currentCompanyDataModel.contact,
        contactPersonCallingCode: companyCallingCode,
        contactPersonPhoneNumber: '',
        contactPersonEmail: currentCompanyDataModel.email,
    };

    const onSubmit = (formState: CompanyInfoStepState) => {
        setReadyToSubmit(true);
        setCompanyInfoStepState?.(formState);
    };

    return (
        <>
            <Alert state="information" className="mb-6">
                <Trans>
                    We were able to find some information about your company in e-conomic, check if it is correct and make sure
                    all fields are completed.
                </Trans>
            </Alert>

            <Form<CompanyInfoStepState>
                onSubmit={onSubmit}
                initialData={formInitialData}
                key="payroll-onboarding-step2-form"
                skipNotifications
                validationSchema={getCompanyInfoStepValidationSchema()}>
                <>
                    <CompanyInfoFormFields formInitialData={formInitialData} />
                    <div className="flex w-full justify-end">
                        <Button type="submit" appearance="primary">
                            <Trans>Next</Trans>
                        </Button>
                    </div>
                </>
            </Form>
            {showCompanyFailedToValidateDialog && (
                <CannotValidateCompanyDialog
                    currentCompanyData={currentCompanyData}
                    onSettingsSaved={() => {
                        onSettingsSaved();
                        setShowCompanyFailedToValidateDialog(false);
                    }}
                />
            )}
        </>
    );
};
